import type { SelectOption } from "@octopusdeploy/design-system-components";
import { Select } from "@octopusdeploy/design-system-components";
import { EnvironmentIcon, EphemeralEnvironmentIcon, ExclamationCircleIcon } from "@octopusdeploy/design-system-icons";
import type { EnvironmentV2Resource } from "@octopusdeploy/octopus-server-client";
import { sortBy } from "lodash";
import { useMemo } from "react";
interface ConfigurableEnvironmentSelectProps {
    allEnvironments: EnvironmentV2Resource[];
    configurableEnvironments: EnvironmentV2Resource[];
    currentEnvironmentId: string | undefined;
    onChange: (newValue: string | undefined) => void;
    disabled?: boolean;
}
export function ConfigurableEnvironmentSelect({ allEnvironments, configurableEnvironments, currentEnvironmentId, onChange, disabled }: ConfigurableEnvironmentSelectProps) {
    const environmentOptions = useMemo(() => getEnvironmentOptions(currentEnvironmentId, allEnvironments, configurableEnvironments), [currentEnvironmentId, allEnvironments, configurableEnvironments]);
    return <Select label={"Environment"} sortItems={false} items={environmentOptions} value={currentEnvironmentId} getOption={(env) => env} onChange={(selected) => onChange(selected)} disabled={disabled}/>;
}
function getEnvironmentOption(environment: EnvironmentV2Resource) {
    if (environment.Type === "Ephemeral") {
        return { value: environment.Id, label: environment.Name, prefix: <EphemeralEnvironmentIcon size={16}/>, description: "Ephemeral Environment" };
    }
    return { value: environment.Id, label: environment.Name, prefix: <EnvironmentIcon size={16}/> };
}
function getEnvironmentOptions(currentEnvironmentId: string | undefined, allEnvironments: EnvironmentV2Resource[], configurableEnvironments: EnvironmentV2Resource[]): SelectOption[] {
    let options: SelectOption[] = sortBy(configurableEnvironments, (e) => e.SortOrder).map(getEnvironmentOption);
    // We only want users to be able to select environments that are related to the project (they're
    // connected to a lifecycle that's in use in the project). This can change at any time (lifecycle
    // update, channel deleted, etc), so the currently configured environment might no longer be
    // in that list.
    //
    // We want them to be able to keep and configure the existing environment if they want, so we add
    // it to the front of the list if it's missing. If they change to a valid environment,
    // the invalid environment will no longer be in the list and that's fine.
    if (currentEnvironmentId && !configurableEnvironments.find((e) => e.Id === currentEnvironmentId)) {
        const environment = allEnvironments.find((e) => e.Id === currentEnvironmentId);
        // The user may not be able to see the current environment because of permissions. That's
        // not a problem, we still want it to work, we just need an option there similar to the missing
        // environment chip.
        options = [{ value: currentEnvironmentId, label: environment ? environment.Name : `Missing Environment (${currentEnvironmentId})`, prefix: <ExclamationCircleIcon size={16}/> }, ...options];
    }
    return options;
}
